export const DEFAULT_SURVEY_SOURCES = [
  { id: "WSP", name: "WSP", urlRequired: false },
  { id: "Marigold", name: "Marigold", urlRequired: true },
];

// Validation error codes. Each value is also the SurveyManagement i18n key the
// drawer renders via t(`SurveyManagement:${error}`), so keep them in sync with
// the locale files. Exported as constants to avoid magic strings/typos.
export const VALIDATION_ERROR_KEYS = {
  REQUIRED: "ValidationRequired",
  INVALID_URL: "ValidationUrl",
  DUPLICATE_ID: "ValidationDuplicateSurveyId",
  DUPLICATE_URL: "ValidationDuplicateSurveyUrl",
};

// Normalizes a value for uniqueness comparison: trims surrounding whitespace and
// lowercases, so trivial case/whitespace variants still count as duplicates.
const norm = (value) => (value || "").trim().toLowerCase();

// Returns true when another survey already uses `value` for `key` (surveyId or
// surveyUrl). `existingSurveys` are mapSurveyToRow outputs, so they carry the same
// field names plus `original.surveyId` (platform id). In edit mode the survey being
// edited is excluded via `editingPlatformId` so it never flags itself.
const hasDuplicate = (existingSurveys, key, value, editingPlatformId) =>
  (existingSurveys || []).some(
    (survey) =>
      survey?.original?.surveyId !== editingPlatformId &&
      norm(survey?.[key]) === norm(value)
  );

export const extractSurveyProviders = (programConfigurations) => {
  const entry = (programConfigurations || []).find(
    (c) => c.key === "partner_configuration"
  );
  const providers = entry?.value?.data?.survey_partner;
  if (Array.isArray(providers)) {
    return providers.map((provider) => ({ id: provider, name: provider }));
  }
  if (providers) {
    return [{ id: providers, name: providers }];
  }
  return [];
};

export const isValidUrl = (value) => {
  if (typeof value !== "string" || value.trim() === "") return false;
  try {
    return Boolean(new URL(value));
  } catch {
    return false;
  }
};

export const validateSurveyForm = (
  values,
  { existingSurveys = [], editingPlatformId = null } = {}
) => {
  const errors = {};
  if (!values.source) errors.source = VALIDATION_ERROR_KEYS.REQUIRED;
  if (!values.surveyName || !values.surveyName.trim())
    errors.surveyName = VALIDATION_ERROR_KEYS.REQUIRED;
  if (!values.surveyId || !values.surveyId.trim()) {
    errors.surveyId = VALIDATION_ERROR_KEYS.REQUIRED;
  } else if (
    hasDuplicate(existingSurveys, "surveyId", values.surveyId, editingPlatformId)
  ) {
    errors.surveyId = VALIDATION_ERROR_KEYS.DUPLICATE_ID;
  }
  const sourceConfig = DEFAULT_SURVEY_SOURCES.find((s) => s.id === values.source);
  const urlRequired = sourceConfig?.urlRequired !== false;

  if (urlRequired) {
    if (!values.surveyUrl || !values.surveyUrl.trim()) {
      errors.surveyUrl = VALIDATION_ERROR_KEYS.REQUIRED;
    } else if (!isValidUrl(values.surveyUrl)) {
      errors.surveyUrl = VALIDATION_ERROR_KEYS.INVALID_URL;
    } else if (
      hasDuplicate(existingSurveys, "surveyUrl", values.surveyUrl, editingPlatformId)
    ) {
      errors.surveyUrl = VALIDATION_ERROR_KEYS.DUPLICATE_URL;
    }
  } else if (values.surveyUrl && values.surveyUrl.trim()) {
    if (!isValidUrl(values.surveyUrl)) {
      errors.surveyUrl = VALIDATION_ERROR_KEYS.INVALID_URL;
    } else if (
      hasDuplicate(existingSurveys, "surveyUrl", values.surveyUrl, editingPlatformId)
    ) {
      errors.surveyUrl = VALIDATION_ERROR_KEYS.DUPLICATE_URL;
    }
  }
  return errors;
};

export const isFormValid = (values, options) =>
  Object.keys(validateSurveyForm(values, options)).length === 0;

export const EMPTY_SURVEY = {
  source: "",
  surveyName: "",
  surveyId: "",
  surveyUrl: "",
};
